import React, { useEffect, useState } from 'react'
import { Container, Typography, Grid2, Paper, FormControl, FormLabel, FormControlLabel, Checkbox, FormGroup, FormHelperText, Skeleton, Alert, Link, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { IconCircle, IconCircleFilled, IconChevronLeft } from '@tabler/icons-react'
import { useForm, Controller } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams, useLocation, useNavigate } from 'react-router-dom'

import CustomButton from '@components/button/customButton'
import ControlledTextField from '@components/inputs/controlledTextField'
import ControlledAutocomplete from '@components/inputs/controlledAutocomplete'
import ControlledFilePicker from '@components/inputs/controlledFilePicker'
import CustomTextField from '@components/inputs/customTextField'
import ConfirmDialog from '@components/dialog/confirmDialog'
import InfoDialog from '@components/dialog/infoDialog'
import ControlledDatePicker from '@components/inputs/controlledDatePicker'

import { useSnackbar } from '@context/snackbarContext'

import { UserApiEndpoints, ToyyibpayUrl } from '@configuration/apiEndpoints'

import { ACTIVITY_PAYMENT_STATUS_GUID, APPLICATION_STATUS_GUID, EVENT_STATUS_GUID, PAYMENT_TERM } from '@utils/constant'

import "./payment.css";

const PAYMENT_FOR = {
  ACTIVITY: 'Activity',
  DONATION: 'Donation'
}

const PAYMENT_METHOD = {
  ONLINE_BANKING: 'Online Banking',
  CARD: 'Card',
  ACTIVITY_BANK_TRANSFER: '380824c3-316d-4ba7-85a9-536d17467eeb',
  DONATION_BANK_TRANSFER: 'ed5f48cf-fad3-4a1c-8e16-a352197e0427',
  ACTIVITY_FPX: '8b04cbb7-9340-4d16-b080-19e3e7d9a387', 
  DONATION_FPX: '584c2d63-d9b5-4434-a214-4296547671f3',
  ACTIVITY_CARD: 'beecc75b-873f-451c-bf6e-41561d140d73', 
  DONATION_CARD: 'a0f5f3fc-353c-42ec-8a02-c755aa9ca7e8',
}

export default function Payment() {
  const { t } = useTranslation()
  const openSnackbar = useSnackbar();
  const { pathname } = useLocation();
  const pageAction = pathname.split("/")[2] || null; // get from pathname expected values ["application", null]
  const { id } = useParams()
  const navigate = useNavigate()

  const [paymentDetails, setPaymentDetails] = useState(null)

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [infoDialogConfig, setInfoDialogConfig] = useState({
    open: false,
    dialogTitle: t('payment-page.acceptanceNoValidated'),
    dialogDescription: t('payment-page.acceptanceNoValidatedDesc'),
  })

  const acceptanceNoForm = useForm({})

  const { handleSubmit, control, getValues, reset, watch, setValue } = useForm({
    defaultValues: {
      paymentFor: {value: 'Activity', label: t('payment-page.activity')},
      tinNo: "",
      tinPartyFullname: "",
      paymentDate: dayjs().format("YYYY-MM-DD")
    }
  })

  const validateMutation = useMutation({
    mutationFn: (data) => {
      return axios.get(`${UserApiEndpoints.validateAcceptanceNo}/${data.acceptanceNo}`, { requiresAuth: false })
    },
    onSuccess: (res) => {
      const data = res.data
      setPaymentDetails(data)
      setInfoDialogConfig({
        open: true,
        dialogTitle: t('payment-page.acceptanceNoValidated'),
        dialogDescription: t('payment-page.acceptanceNoValidatedDesc'),
      })
    },
    onError: (err) => {
      openSnackbar(t('payment-page.acceptanceNoValidateFailed'), "error")
    }
  })

  const fetchApplicationMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${UserApiEndpoints.getApplication}/${id}`, { requiresAuth: false })
    },
    onSuccess: (res) => {
      const data = res.data
      setPaymentDetails(data)
    },
    onError: (err) => {
      openSnackbar(t('payment-page.fetchApplicationFailed'), "error")
    }
  })

  const createActivityPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${UserApiEndpoints.createActivityPayment}`, data,  {headers: {"Content-Type": "multipart/form-data", requiresAuth: false }})
    },
    onSuccess: (res) => {
      setPaymentDetails(null)
      reset()
      acceptanceNoForm.reset()
      setOpenConfirmDialog(false)
      setInfoDialogConfig({
        open: true,
        dialogTitle: t('payment-page.paymentSubmitted'),
        dialogDescription: t('payment-page.paymentSubmittedDesc'),
      })
      if(id && pageAction === "application"){
        fetchApplicationMutation.mutate(id)
      }
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar(t('payment-page.paymentSubmitFailed'), "error")
    }
  })

  const createActivityPaymentGatewayMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${UserApiEndpoints.createActivityPaymentGateway}`, data, {headers: { requiresAuth: false }})
    },
    onSuccess: (res) => {
      setOpenConfirmDialog(false)
      // redirect
      const billCode = res.data.billCode
      window.location.href = `${ToyyibpayUrl}/${billCode}`;
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar(t('payment-page.paymentSubmitFailed'), "error")
    }
  })

  const remakeActivityPaymentGatewayMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${UserApiEndpoints.remakeActivityPaymentGateway}`, data, {headers: { requiresAuth: false }})
    },
    onSuccess: (res) => {
      setOpenConfirmDialog(false)
      // redirect
      const billCode = res.data.billCode
      window.location.href = `${ToyyibpayUrl}/${billCode}`;
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar(t('payment-page.paymentSubmitFailed'), "error")
    }
  })

  const createDonationPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${UserApiEndpoints.createDonationPayment}`, data,  {headers: {"Content-Type": "multipart/form-data", requiresAuth: false }})
    },
    onSuccess: (res) => {
      setPaymentDetails(null)
      reset()
      acceptanceNoForm.reset()
      setOpenConfirmDialog(false)
      setInfoDialogConfig({
        open: true,
        dialogTitle: t('payment-page.paymentSubmitted'),
        dialogDescription: t('payment-page.paymentSubmittedDesc'),
      })
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar(t('payment-page.paymentSubmitFailed'), "error")
    }
  })

  const createDonationPaymentGatewayMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${UserApiEndpoints.createDonationPaymentGateway}`, data, {headers: { requiresAuth: false }})
    },
    onSuccess: (res) => {
      setOpenConfirmDialog(false)
      // redirect
      const billCode = res.data.billCode
      window.location.href = `${ToyyibpayUrl}/${billCode}`;
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar(t('payment-page.paymentSubmitFailed'), "error")
    }
  })

  const activityPaymentMethodOptions = useQuery({
    queryKey: ['getActivityPaymentMethod'],
    queryFn: () =>
      axios.get(UserApiEndpoints.getActivityPaymentMethod, { requiresAuth: false }),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data
      .sort((a, b) => b.valueEng.localeCompare(a.valueEng)) // Sort by valueEng in descending order
      .map(item => ({
        value: item.parameterGuid,
        label: item.valueEng
      }));
    },
  })

  const donationPaymentMethodOptions = useQuery({
    queryKey: ['getDonationPaymentMethod'],
    queryFn: () =>
      axios.get(UserApiEndpoints.getDonationPaymentMethod, { requiresAuth: false }),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data
      .sort((a, b) => b.valueEng.localeCompare(a.valueEng))
      .map(item => ({
        value: item.parameterGuid,
        label: item.valueEng
      }));
    },
  })

  const bankOptions = useQuery({
    queryKey: ['getBank'],
    queryFn: () =>
      axios.get(UserApiEndpoints.getBank, { requiresAuth: false }),
    initialData: [],
    select: (res) => {
      return res?.data?.filter(item => item.isActivity === true) || [];
    },
  })

  const fundOptions = useQuery({
    queryKey: ['getFund'],
    queryFn: () =>
      axios.get(UserApiEndpoints.getFund, { requiresAuth: false }),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.map(item => ({
        value: item.fundGuid,
        label: item[`title${t('param')}`],
        banks: item.fundBanks.map(item => ({
          ...item.bank
        }))
    }));
    },
  })

  const onValidateAcceptNo = (data) => {
    validateMutation.mutate(data)
  }

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues()
    const formData = new FormData()
    
    formData.append('paymentMethodGuid', data?.paymentMethod.value)
    formData.append('tinNo', data?.tinNo)
    formData.append('tinPartyFullname', data?.tinPartyFullname)
    formData.append('paymentDate', dayjs(data.paymentDate).format("YYYY-MM-DD"))
    
    if(data.paymentFor.value === PAYMENT_FOR.ACTIVITY){
      formData.append('applicationGuid', paymentDetails?.applicationGuid)
      if(data?.paymentMethod.value === PAYMENT_METHOD.ACTIVITY_BANK_TRANSFER){
        formData.append('bankGuid', data?.bank)
        formData.append('payslip', data?.document)

        createActivityPaymentMutation.mutate(formData)
      }
      else{ //PAYMENT_METHOD.ACTIVITY_FPX or PAYMENT_METHOD.ACTIVITY_CARD
        formData.append('billPaymentChannel', data?.paymentMethod.value === PAYMENT_METHOD.ACTIVITY_CARD ? '1' : '0')
        formData.append('postFeesOptionGuid', paymentDetails?.postFeeOption?.postFeeOptionGuid)
        formData.append('billReturnUrl', `${window.location.origin}/payment/status/application${paymentDetails?.post?.paymentTerm === PAYMENT_TERM.POSTPAYMENT ? '' : '/'+paymentDetails?.applicationGuid}`)
        //activityPayment?.paymentStatus?.parameterGuid
        if(paymentDetails?.activityPayment?.paymentStatus?.parameterGuid === ACTIVITY_PAYMENT_STATUS_GUID.PENDING){
          formData.append('activityPaymentGuid', paymentDetails?.activityPayment?.activityPaymentGuid)
          remakeActivityPaymentGatewayMutation.mutate(formData)
        }
        else{
          createActivityPaymentGatewayMutation.mutate(formData)
        }
      }
    }
    else if(data.paymentFor.value === PAYMENT_FOR.DONATION){
      formData.append('fundGuid', data?.donationFund?.value)
      formData.append('amount', data?.donationAmount)
      formData.append('customer.customerName', data?.fullname)
      formData.append('customer.emailAddress', data?.email)
      formData.append('customer.contactNumber', data?.contactNumber)
      if(data?.paymentMethod.value === PAYMENT_METHOD.DONATION_BANK_TRANSFER){
        formData.append('bankGuid', data?.bank)
        formData.append('payslip', data?.document)

        createDonationPaymentMutation.mutate(formData)
      }
      else{
        formData.append('billPaymentChannel', data?.paymentMethod.value === PAYMENT_METHOD.DONATION_CARD ? '1' : '0')
        formData.append('billReturnUrl', `${window.location.origin}/payment/status/donation`)
        createDonationPaymentGatewayMutation.mutate(formData)
      }
    }
      
  }

  const watchPaymentFor = watch('paymentFor')
  const watchPaymentMethod = watch('paymentMethod')

  const watchAcceptNo = acceptanceNoForm.watch('acceptanceNo')

  const watchDonationFund = watch('donationFund')


  useEffect(()=>{
    if(id && pageAction === "application"){
      fetchApplicationMutation.mutate(id)
    }
    else{
      setPaymentDetails(null)
    }
  }, [id, pageAction])

  useEffect(()=>{
    setValue('paymentMethod', null)
    setValue('bank', null)
  }, [watchPaymentFor])

  useEffect(()=>{
    setValue('bank', null)
  }, [watchDonationFund])
  
  const isSkeleton = !(paymentDetails) && pageAction === "application"

  const disabledPayment = (
    (watchPaymentFor.value === PAYMENT_FOR.ACTIVITY || pageAction === "application") 
    &&
    (
      !paymentDetails ||
      (pageAction === "application" ?
        paymentDetails?.post?.paymentTerm === PAYMENT_TERM.POSTPAYMENT ||
        (paymentDetails?.applicationStatus?.parameterGuid !== APPLICATION_STATUS_GUID.DRAFT) ||
        (paymentDetails?.activityPayment ? (paymentDetails?.activityPayment?.paymentStatus?.parameterGuid !== ACTIVITY_PAYMENT_STATUS_GUID.INVALID && paymentDetails?.activityPayment?.paymentStatus?.parameterGuid !== ACTIVITY_PAYMENT_STATUS_GUID.PENDING) : false) ||
        paymentDetails?.post?.eventStatus?.parameterGuid !== EVENT_STATUS_GUID.OPENED
      : false
      )
    )
  );

  const errorMsg = (disabledPayment && paymentDetails && pageAction === "application") ? (
    paymentDetails?.post?.paymentTerm === PAYMENT_TERM.POSTPAYMENT ? t('payment-page.wrongPaymentTermAlert') :
    paymentDetails?.applicationStatus?.parameterGuid === APPLICATION_STATUS_GUID.APPROVED ? t('payment-page.paymentApprovedAlert') : 
    paymentDetails?.applicationStatus?.parameterGuid === APPLICATION_STATUS_GUID.REJECTED ? t('payment-page.applicationRejectAlert') :
    paymentDetails?.applicationStatus?.parameterGuid === APPLICATION_STATUS_GUID.EXPIRED ? t('payment-page.applicationExpiredAlert') :
    [ACTIVITY_PAYMENT_STATUS_GUID.PAID, ACTIVITY_PAYMENT_STATUS_GUID.PAID_WHEN_CREATED].includes(paymentDetails?.activityPayment?.paymentStatus?.parameterGuid ?? '') ? t('payment-page.paymentMadeAlert') :
    [ACTIVITY_PAYMENT_STATUS_GUID.PENDING_REFUND].includes(paymentDetails?.activityPayment?.paymentStatus?.parameterGuid ?? '') ? t('payment-page.processingRefundAlert') :
    [ACTIVITY_PAYMENT_STATUS_GUID.REFUNDED].includes(paymentDetails?.activityPayment?.paymentStatus?.parameterGuid ?? '') ? t('payment-page.paymentRefundedAlert') :
    [ACTIVITY_PAYMENT_STATUS_GUID.VOIDED].includes(paymentDetails?.activityPayment?.paymentStatus?.parameterGuid ?? '') ? t('payment-page.paymentVoidedAlert') :
    [ACTIVITY_PAYMENT_STATUS_GUID.SUBMITTED].includes(paymentDetails?.activityPayment?.paymentStatus?.parameterGuid ?? '') ? t('payment-page.reviewingPaymentAlert') :
    paymentDetails?.post?.eventStatus?.parameterGuid !== EVENT_STATUS_GUID.OPENED ? t('payment-page.registrationClosedAlert') : null
  ) : null

  const filteredPaymentMethods = paymentDetails?.activityPayment?.paymentStatus?.parameterGuid === ACTIVITY_PAYMENT_STATUS_GUID.PENDING
  ? activityPaymentMethodOptions.data.filter(method => method.value !== PAYMENT_METHOD.ACTIVITY_BANK_TRANSFER)
  : activityPaymentMethodOptions.data;

  return (
    <Container className='payment'>
      {
        pageAction === "application" ?
        <>
        <Link onClick={()=>navigate(`/activity${paymentDetails?.post?.postGuid ? `/${paymentDetails?.post?.postGuid}` : ''}`, { replace: true })} underline="hover" color='#414042' fontSize={'14px'} style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
          <IconChevronLeft size={15}/>{t('activity-page.back')}
        </Link>
        <Typography className='category'>{t('payment-page.payment')}</Typography>
        <Typography className='title' pb={'15px'}>{t('payment-page.paymentActivity')}</Typography>
        </>
        :
        <>
        <Typography className='title'>{t('payment-page.payment')}</Typography>
        <Typography className='description'>{t('payment-page.paymentInstruction')}</Typography>
        </>
      }
      <form onSubmit={handleSubmit(onSubmit)} className='content-wrapper'>
        {isSkeleton ?
          <Skeleton animation={!fetchApplicationMutation.isError && 'pulse'} variant="rounded" height={"300px"} style={{borderRadius: '10px'}}/>
        :
        <>
          {errorMsg &&
            <Alert severity="warning">
              {errorMsg}
            </Alert>
          }
          <Paper className='content-card'>
            <Grid2 container spacing={'25px'}>
              <Typography className='content-title'>{t('payment-page.paymentDetails')}</Typography>
              {pageAction !== "application" && 
                <Grid2 size={12}>
                  <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                    <ControlledAutocomplete 
                      name="paymentFor"
                      control={control}
                      label={t('payment-page.paymentFor')}
                      variant='standard'
                      size='medium'
                      options={[
                        {value: 'Activity', label: t('payment-page.activity')},
                        {value: 'Donation', label: t('payment-page.donation')},
                      ]}
                      rules={{
                        required: t('payment-page.paymentForRequired')
                      }}
                      disableClearable
                      // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                    />
                  </Grid2>
                </Grid2>
              }
              { (watchPaymentFor?.value === PAYMENT_FOR.ACTIVITY) && <>
                {pageAction !== "application" && 
                <Grid2 size={12}>
                  <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                    <ControlledTextField 
                      name="acceptanceNo"
                      control={acceptanceNoForm.control}
                      label={t('payment-page.acceptanceNo')}
                      variant='standard'
                      size='medium'
                      rules={{
                        required: t('payment-page.acceptanceNoRequired')
                      }}
                      // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                      disabled={paymentDetails !== null}
                    />
                  </Grid2>
                </Grid2>
                }
                { 
                (!paymentDetails && pageAction !== "application") ? 
                  <Grid2 size={12}>
                    <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }} display={'flex'} justifyContent={"flex-end"} gap={"10px"}>
                      <CustomButton onClick={acceptanceNoForm.handleSubmit(onValidateAcceptNo)} disabled={!watchAcceptNo}>{t('payment-page.validateAcceptanceNo')}</CustomButton>
                    </Grid2>
                  </Grid2>
                :
                <>
                  <Grid2 size={12}>
                    <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                      <CustomTextField 
                        value={paymentDetails?.post?.postName || '-'}
                        label={t('payment-page.activityName')}
                        variant='standard'
                        size='medium'
                        readOnly={true}
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 size={12}>
                    <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                      <CustomTextField 
                        value={paymentDetails?.postFeeOption[`optionName${t('param')}`] || '-'}
                        label={t('payment-page.activityOption')}
                        variant='standard'
                        size='medium'
                        readOnly={true}
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 size={12}>
                    <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                      <CustomTextField 
                        value={paymentDetails?.postFeeOption?.fee || '-'}
                        label={t('payment-page.activityOptionFee')}
                        variant='standard'
                        size='medium'
                        readOnly={true}
                      />
                    </Grid2>
                  </Grid2>
                </>
                }
              </>}

              { watchPaymentFor?.value === PAYMENT_FOR.DONATION && <>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledAutocomplete 
                    name="donationFund" //  Donation Fund
                    control={control}
                    label={t('payment-page.selectDonationFund')}
                    variant='standard'
                    size='medium'
                    loading={fundOptions.isFetching}
                    options={fundOptions.data}
                    rules={{
                      required: t('payment-page.paymentForRequired')
                    }}
                    disabled={!watchPaymentFor}
                    getOptionKey={(option) => option.value}
                  />
                </Grid2>
              </Grid2>
              </>}
            </Grid2>
          </Paper>
        </>
        }
        { watchPaymentFor?.value === PAYMENT_FOR.ACTIVITY && <>
          <Paper className='content-card'>
            <Grid2 container spacing={'25px'}>
              <Typography className='content-title'>{t('payment-page.additionalDetails')}</Typography>

              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="tinNo"
                    control={control}
                    label={t('payment-page.tinNo')}
                    variant='standard'
                    size='medium'
                    rules={{
                      validate: (tinNo, { tinPartyFullname }) => {
                        if (!tinNo && tinPartyFullname) return "TIN No is required if TIN Party Fullname is filled"
                        return true
                      },
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="tinPartyFullname"
                    control={control}
                    label={t('payment-page.tinPartyFullname')}
                    variant='standard'
                    size='medium'
                    rules={{
                      validate: (tinPartyFullname, { tinNo }) => {
                        if (tinNo && !tinPartyFullname) return "TIN Party Fullname is required if TIN No is filled"
                        return true
                      },
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          </Paper>
        </> }

        { watchPaymentFor?.value === PAYMENT_FOR.DONATION && <>
          <Paper className='content-card'>
            <Grid2 container spacing={'25px'}>
              <Typography className='content-title'>{t('payment-page.donationDetails')}</Typography>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="fullname"
                    control={control}
                    label={t('payment-page.fullName')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('payment-page.fullNameRequired'),
                      pattern: {
                        value: /^[A-Za-z\s'-]+$/, // ✅ Only A-Z, a-z, '-, and spaces
                        message: t('register-page.fullNameLettersOnly'),
                      },
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="email"
                    control={control}
                    label={t('payment-page.emailAddress')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('payment-page.emailAddressRequired'),
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: t('payment-page.emailAddressInvalid'),
                      }
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="contactNumber"
                    control={control}
                    label={t('register-page.contactNumber')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('register-page.contactNumberRequired'),
                      pattern: {
                        value: /^[0-9]+$/,
                        message: t('register-page.contactNumberInvalid')
                      }
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="tinNo"
                    control={control}
                    label={t('payment-page.tinNo')}
                    variant='standard'
                    size='medium'
                    rules={{
                      validate: (tinNo, { tinPartyFullname }) => {
                        if (!tinNo && tinPartyFullname) return "TIN No is required if TIN Party Fullname is filled"
                        return true
                      },
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="tinPartyFullname"
                    control={control}
                    label={t('payment-page.tinPartyFullname')}
                    variant='standard'
                    size='medium'
                    rules={{
                      validate: (tinPartyFullname, { tinNo }) => {
                        if (tinNo && !tinPartyFullname) return "TIN Party Fullname is required if TIN No is filled"
                        return true
                      },
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="donationAmount"
                    control={control}
                    label={t('payment-page.donationAmount')}
                    type='number'
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('payment-page.donationAmountRequired'),
                      min: {
                        value: 1,
                        message: t('payment-page.donationAmountInvalid')
                      },
                      max: {
                        value: 30000,
                        message: t('payment-page.donationAmountInvalid') // Add a new translation key
                      },
                      valueAsNumber: true // Ensures the value is treated as a number
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          </Paper>
        </> }
        
        <Paper className='content-card'>
          <Grid2 container spacing={'25px'}>
            <Typography className='content-title'>{t('payment-page.paymentMethod')}</Typography>
            <Grid2 size={12}>
              <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                <ControlledAutocomplete 
                  name="paymentMethod"
                  control={control}
                  label={t('payment-page.paymentMethod')}
                  variant='standard'
                  size='medium'
                  loading={activityPaymentMethodOptions.isFetching || donationPaymentMethodOptions.isFetching}
                  options={watchPaymentFor?.value === PAYMENT_FOR.ACTIVITY ? filteredPaymentMethods : donationPaymentMethodOptions.data}
                  rules={{
                    required: t('payment-page.paymentMethodRequired')
                  }}
                  disableClearable
                  // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                />
              </Grid2>
            </Grid2>
            { [PAYMENT_METHOD.ACTIVITY_BANK_TRANSFER, PAYMENT_METHOD.DONATION_BANK_TRANSFER].includes(watchPaymentMethod?.value) && <>
            <Grid2 size={12}>
              <Controller
                name={'bank'}
                control={control}
                defaultValue={false}
                rules={{ required: t('payment-page.transferToRequired') }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <FormControl
                    error={error}
                    fullWidth
                  >
                  <FormLabel component="legend" style={{fontSize: '12px', marginBottom: '10px'}}>{t('payment-page.transferTo')}</FormLabel>
                  <Grid2 container size={{ lg: 10, md: 10,  sm: 12, xs: 12 }} spacing={'15px'}>
                  {watchPaymentFor?.value === PAYMENT_FOR.ACTIVITY && 
                    <>
                    {bankOptions.data.map((data, index)=>(
                      <Grid2 size={{ lg: 6, md: 6,  sm: 12, xs: 12 }} key={index}>
                        <div onClick={()=>onChange(data.bankGuid)} className={`bank-card ${value === data?.bankGuid && 'active'}`}>
                          {value === data?.bankGuid ? <IconCircleFilled color='#7CA7AD' /> : <IconCircle color='#E5E5E5' />}
                          <div>
                            <Typography className='name'>{data?.bankName}</Typography>
                            <Typography className='account'>{data?.accountNumber}</Typography>
                          </div>
                        </div>
                      </Grid2>
                    ))}
                    {bankOptions.data.length === 0 &&
                      <Grid2 size={{ lg: 6, md: 6,  sm: 12, xs: 12 }}>
                        <div style={{width: '100%', height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F5F5F5', borderRadius: 5}}>
                        {
                          bankOptions.isFetching ?
                          <CircularProgress />
                          :
                          <Typography className='name'>{t('payment-page.noBankOptions')}</Typography>
                        }
                        </div>
                      </Grid2>
                    }
                    </>
                  }
                  {watchPaymentFor?.value === PAYMENT_FOR.DONATION && 
                    <>
                    {watchDonationFund?.banks?.map((data, index)=>(
                      <Grid2 size={{ lg: 6, md: 6,  sm: 12, xs: 12 }} key={index}>
                        <div onClick={()=>onChange(data.bankGuid)} className={`bank-card ${value === data?.bankGuid && 'active'}`}>
                          {value === data?.bankGuid ? <IconCircleFilled color='#7CA7AD' /> : <IconCircle color='#E5E5E5' />}
                          <div>
                            <Typography className='name'>{data?.bankName}</Typography>
                            <Typography className='account'>{data?.accountNumber}</Typography>
                          </div>
                        </div>
                      </Grid2>
                    ))}
                    {!watchDonationFund &&
                      <Grid2 size={{ lg: 6, md: 6,  sm: 12, xs: 12 }}>
                        <div style={{width: '100%', height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F5F5F5', borderRadius: 5}}>
                          <Typography className='name'>{t('payment-page.pleaseSelectDonationFund')}</Typography>
                        </div>
                      </Grid2>
                    }
                    </>
                  }
                  </Grid2>
                  {error &&
                    <FormHelperText variant='standard'>{error.message}</FormHelperText>
                  }
                  </FormControl>
                )}/>
            </Grid2>
            <Grid2 size={12}>
              <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                <ControlledFilePicker 
                  name="document"
                  control={control}
                  label={t('payment-page.uploadDocument')}
                  // disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
                  type="file"
                  variant='standard'
                  size='medium'
                  rules={{
                    required: "A valid file is required",
                    validate: (file) => {
                      if (!file || !(file instanceof File)) {
                        return "A valid file is required";
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        return "File size must be less than 5MB";
                      }
                      if (!["image/jpeg", "image/png", "application/pdf"].includes(file.type)) {
                        return "File type must be JPEG, PNG, or PDF";
                      }
                      return true;
                    },
                  }}
                />
              </Grid2>
            </Grid2>
            <Grid2 size={12}>
              <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                <ControlledDatePicker 
                  name="paymentDate"
                  control={control}
                  label={t('payment-page.paymentDate')}
                  variant='standard'
                  size='medium'
                  rules={{
                    required: t('payment-page.paymentDateRequired'),
                    validate: (value) => {
                      const date = new Date(value);
                      return !isNaN(date.getTime()) || t('payment-page.paymentDateInvalid');
                    }
                  }}
                />
              </Grid2>
            </Grid2>
            </> }
          </Grid2>
          
        </Paper>
        <Paper className='content-card'>
          <Grid2 container spacing={'10px'}>
            <Grid2 size={12}>
              <Grid2 size={12}>
                <Controller
                  name={'isCorrect'}
                  control={control}
                  defaultValue={false}
                  rules={{ required: t('payment-page.agreeCorrectInfoRequired') }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl
                      error={error}
                      fullWidth
                    >
                      <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...field}
                                checked={field.value}
                                size='small'
                              />
                            }
                            label={
                              <Typography fontSize={'14px'}>
                                {t('payment-page.agreeCorrectInfo')}
                              </Typography>
                            }
                          />
                      </FormGroup>
                      {error &&
                        <FormHelperText variant='standard'>{error.message}</FormHelperText>
                      }
                    </FormControl>
                  )}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </Paper>
        <Paper className='content-card'>
          <CustomButton 
            type="submit" 
            disabled={disabledPayment} 
            loading={
              createActivityPaymentMutation.isPending ||
              createDonationPaymentMutation.isPending ||
              createActivityPaymentGatewayMutation.isPending
            }
          >
            {t('payment-page.proceedPayment')}
          </CustomButton>
        </Paper>
      </form>
      <ConfirmDialog 
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        onSubmit={onConfirmSubmit}
        dialogTitle={t('payment-page.submitPayment')}
        dialogDescription={t('payment-page.submitPaymentConfirmation')}
        cancelText={t('cancel')}
        confirmText={t('confirm')}
        loading={
          createActivityPaymentMutation.isPending ||
          createDonationPaymentMutation.isPending ||
          createActivityPaymentGatewayMutation.isPending
        }
      />
      <InfoDialog 
        open={infoDialogConfig.open}
        onClose={()=>setInfoDialogConfig(prev => ({...prev, open: false}))}
        dialogTitle={infoDialogConfig.dialogTitle}
        dialogDescription={infoDialogConfig.dialogDescription}
        closeText={t('ok')}
      />
    </Container>
  )
}
